<template>
  <mu-card class="aio-input" id="blood-glucose-input">
    <mu-card-actions class="aio-input-header pointer" @click.native="openInput">
      <mu-flexbox justify="flex_start" style="height: 48px">
        <svg class="card-left-icon" width="14px" height="16px" viewBox="26 191 11 16" version="1.1"
             xmlns="http://www.w3.org/2000/svg">
          <path
            d="M36.3873266,202.013826 C36.3873266,201.047215 36.0280503,199.003253 34.5350319,197.082478 C33.2459103,195.424041 31.1433712,191 31.1433712,191 C31.1433712,191 29.1414164,195.424041 27.8522947,197.082448 C26.3592763,199.003223 26,201.047186 26,202.013826 C26,204.564452 28.2936491,206.63741 31.1433712,206.685209 L31.1433712,206.686322 C31.1601681,206.686322 31.1768993,206.685883 31.1936633,206.685766 C31.2104602,206.685912 31.2271914,206.686322 31.2439883,206.686322 L31.2439883,206.685209 C34.0936775,206.63741 36.3873266,204.564452 36.3873266,202.013826 L36.3873266,202.013826 Z"
            id="Shape" stroke="none" fill="#E74C3C" fill-rule="evenodd"></path>
        </svg>
        <mu-flexbox-item class="left16" v-text="title"></mu-flexbox-item>
        <mu-flexbox-item
          align="right"
        >
          <mu-icon-button
            v-if="!isOpenInput"
            v-tooltip.top="isOpenInput ? $t('message.closeText') : $t('message.addEntryText')"
            class="card-bar-icon blueIcon"
            :icon="'add_circle'"
          />
          <mu-icon-button
            v-if="isOpenInput"
            class="card-bar-icon blueIcon"
            :icon="'remove_circle_outline'"
          />
        </mu-flexbox-item>
      </mu-flexbox>
    </mu-card-actions>
    <form style="overflow: hidden" v-if="isOpenInput" novalidate>
      <mu-card-text>
        <mu-row>
          <mu-col width="100" tablet="50" desktop="50"
                  v-if="$store.state.user.settings.blood_glucose.measure === 'mgdl'">
            <mu-text-field
              type="number"
              v-validate
              min="0"
              max="540"
              :data-vv-rules="'required|min_value:0|max_value:540'"
              data-vv-as="mg/dL"
              data-vv-name="mgdl"
              data-vv-value-path="inputValue"
              :errorText="errors.first('mgdl')"
              :label="$t('message.labelBloodSugar')"
              v-model.number="model.blood_glucose_mgdl"
              :hintText="$t('message.placeholderText')"
              :fullWidth="true"
              :helpText="$store.state.user.settings.blood_glucose.unit"
            />
          </mu-col>
          <mu-col width="100" tablet="50" desktop="50"
                  v-if="$store.state.user.settings.blood_glucose.measure === 'mmoll'">
            <mu-text-field
              type="number"
              v-validate
              min="0"
              max="30"
              data-vv-as="mmol/L"
              :data-vv-rules="'required|min_value:0|max_value:30'"
              data-vv-name="mmoll"
              data-vv-value-path="inputValue"
              :errorText="errors.first('mmoll')"
              :label="$t('message.labelBloodSugar')"
              v-model.number="model.blood_glucose_mmoll"
              :hintText="$t('message.placeholderText')"
              :fullWidth="true"
              :helpText="$store.state.user.settings.blood_glucose.unit"
            />
          </mu-col>
          <mu-col width="100" tablet="50" desktop="50"
                  v-if="$store.state.user.settings.blood_glucose.measure === 'percentage'">
            <mu-text-field
              type="number"
              v-validate
              min="0"
              max="25"
              :data-vv-rules="'min_value:0|max_value:25'"
              data-vv-name="hba1c_percent"
              :data-vv-as="hba1c.unit"
              data-vv-value-path="inputValue"
              :errorText="errors.first('hba1c_percent')"
              :label="$t('message.labelHba1cOptional')"
              v-model.number="model.hba1c_percent"
              :hintText="$t('message.placeholderText')"
              :fullWidth="true"
              :helpText="$store.state.user.settings.hba1c.unit"
            />
          </mu-col>
          <mu-col width="100" tablet="50" desktop="50"
                  v-if="$store.state.user.settings.blood_glucose.measure === 'mmol-mol'">
            <mu-text-field
              type="number"
              v-validate
              min="0"
              max="300"
              :data-vv-rules="'min_value:0|max_value:300'"
              data-vv-name="hba1c_mmol_mol"
              :data-vv-as="$store.state.user.settings.hba1c.unit"
              data-vv-value-path="inputValue"
              :errorText="errors.first('hba1c_mmol_mol')"
              :label="$t('message.labelHba1cOptional')"
              v-model.number="model.hba1c_mmol_mol"
              :hintText="$t('message.placeholderText')"
              :fullWidth="true"
              :helpText="$store.state.user.settings.hba1c.unit"
            />
          </mu-col>
        </mu-row>
        <mu-select-field
          multiple
          separator=", "
          v-model="model.tags"
          :label="$t('message.labelTagYourReading')"
          :hintText="$t('message.selectText')"
          :fullWidth="true"
          :maxHeight="300"
          :scroller="scroller"
        >
          <mu-menu-item
            v-for="tag, index in readingTags"
            :key="'reading-tag-' + index"
            :value="tag"
            :title="$t(localizeTag(tag.tag))"
          />
        </mu-select-field>
      </mu-card-text>
    </form>
  </mu-card>
</template>
<script type="text/babel">

  import TagLocalizerMixin from '../../../../mixins/Localization/BgTags/TagLocalizerMixin'
  import ListsApi from '../../../../api/Lists'



  import {TweenMax, Power2, TimelineLite} from 'gsap'


  export default {
    mixins: [TagLocalizerMixin],
    name: 'aio-bloodGlucose',
    data() {
      return {
        title: this.$t('message.cardBloodGlucose'),
        isOpenInput: false,
        scroller: document.getElementsByClassName('main-content')[0],
        readingTags: [],
        model: {
          blood_glucose_mgdl: null,
          blood_glucose_mmoll: null,
          tags: []
        }
      }
    },
    watch: {
      'model.blood_glucose_mgdl': function () {
        this.trySubmitAndEmitValue();
      },
      'model.blood_glucose_mmoll': function () {
        this.trySubmitAndEmitValue();
      },
      'model.tags': function () {
        this.trySubmitAndEmitValue();
      }
    },
    mounted() {
      let self = this;
      self.loadTags();
    },
    methods: {
      trySubmitAndEmitValue() {
        let self = this;
        self.$validator.validateAll()
          .then(function (valid) {
            if (valid === true) {
              let model = {};
              if (self.$store.state.user.settings.blood_glucose.measure === 'mgdl') {
                model.blood_glucose_mgdl = self.model.blood_glucose_mgdl;
                model.tags = self.model.tags;
              }
              if (self.$store.state.user.settings.blood_glucose.measure === 'mmoll') {
                model.blood_glucose_mmoll = self.model.blood_glucose_mmoll;
                model.tags = self.model.tags;
              }
              self.$emit('onValid', model);
            }
            else {
              self.$emit('onValid', null);
            }
          })
      },
      loadTags() {
        let self = this;
        let getData = ListsApi.getTagList();
        getData.then(function (response) {
          let data = response.data;
          data = data.sort(function (a, b) {
            let textA = a.tag.toUpperCase();
            let textB = b.tag.toUpperCase();
            return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
          });
          self.readingTags = data
        });
      },
      openInput() {
        let self = this;
        self.isOpenInput = !self.isOpenInput;
      },
      closeInput() {
        let self = this;
        this.model = {
          tags: []
        };
        self.isOpenInput = false;
      }
    }
  }
</script>




// WEBPACK FOOTER //
// BloodGlucose.vue?4abe5a25